import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../seo';
import { seoConfigs } from '../../utils/seo-configs';

function SpecialtiesPage() {
  const specialties = [
    {
      id: "anxiety",
      name: "Anxiety Disorders",
      icon: "🧠",
      description: "Treatment for generalized anxiety, panic disorders, social anxiety, and phobias.",
      conditions: ["Generalized Anxiety", "Panic Disorder", "Social Anxiety", "Phobias", "OCD"],
      approaches: ["CBT", "Exposure Therapy", "Mindfulness", "EMDR"]
    },
    {
      id: "depression",
      name: "Depression",
      icon: "🌧️",
      description: "Support for major depression, seasonal depression, and mood disorders.",
      conditions: ["Major Depression", "Seasonal Depression", "Bipolar Disorder", "Dysthymia"],
      approaches: ["CBT", "IPT", "DBT", "Psychodynamic Therapy"]
    },
    {
      id: "trauma",
      name: "Trauma & PTSD",
      icon: "🛡️",
      description: "Specialized care for trauma survivors and PTSD recovery.",
      conditions: ["PTSD", "Complex Trauma", "Childhood Trauma", "Combat Trauma"],
      approaches: ["EMDR", "Trauma-Focused CBT", "Somatic Therapy", "CPT"]
    },
    {
      id: "couples",
      name: "Couples Therapy",
      icon: "💕",
      description: "Relationship counseling to strengthen communication and resolve conflicts.",
      conditions: ["Communication Issues", "Infidelity", "Pre-marital Counseling", "Divorce Support"],
      approaches: ["Gottman Method", "EFT", "Imago Therapy", "Solution-Focused"]
    },
    {
      id: "family",
      name: "Family Therapy",
      icon: "👨‍👩‍👧‍👦",
      description: "Support for family dynamics, parenting challenges, and family conflicts.",
      conditions: ["Family Conflict", "Parenting Issues", "Blended Families", "Teen Issues"],
      approaches: ["Structural Therapy", "Strategic Therapy", "Narrative Therapy", "Systems Therapy"]
    },
    {
      id: "addiction",
      name: "Addiction & Substance Abuse",
      icon: "🔗",
      description: "Recovery support for substance abuse and behavioral addictions.",
      conditions: ["Alcohol Addiction", "Drug Abuse", "Gambling", "Technology Addiction"],
      approaches: ["12-Step Program", "CBT", "Motivational Interviewing", "SMART Recovery"]
    },
    {
      id: "adhd",
      name: "ADHD & Learning",
      icon: "🎯",
      description: "Support for ADHD, learning disabilities, and executive functioning.",
      conditions: ["ADHD", "Learning Disabilities", "Executive Function", "Academic Stress"],
      approaches: ["CBT", "Behavioral Therapy", "Coaching", "Mindfulness"]
    },
    {
      id: "eating",
      name: "Eating Disorders",
      icon: "🍎",
      description: "Treatment for anorexia, bulimia, binge eating, and body image issues.",
      conditions: ["Anorexia", "Bulimia", "Binge Eating", "Body Dysmorphia"],
      approaches: ["CBT-E", "DBT", "FBT", "Nutritional Counseling"]
    },
    {
      id: "lgbtq",
      name: "LGBTQ+ Support",
      icon: "🏳️‍🌈",
      description: "Affirming therapy for LGBTQ+ individuals and communities.",
      conditions: ["Identity Issues", "Coming Out", "Gender Dysphoria", "Discrimination"],
      approaches: ["Affirmative Therapy", "CBT", "Narrative Therapy", "Group Therapy"]
    },
    {
      id: "grief",
      name: "Grief & Loss",
      icon: "🕊️",
      description: "Support through bereavement, loss, and major life transitions.",
      conditions: ["Death of Loved One", "Divorce", "Job Loss", "Life Transitions"],
      approaches: ["Grief Therapy", "CBT", "Narrative Therapy", "Support Groups"]
    }
  ];

  const ageGroups = [
    {
      name: "Children (5-12)",
      icon: "🧸",
      description: "Play therapy and child-friendly approaches",
      specialties: ["Play Therapy", "Behavioral Issues", "School Problems", "Family Changes"]
    },
    {
      name: "Teens (13-17)",
      icon: "📱",
      description: "Adolescent-focused therapy and support",
      specialties: ["Identity Issues", "Peer Pressure", "Academic Stress", "Mood Disorders"]
    },
    {
      name: "Adults (18-64)",
      icon: "💼",
      description: "Individual and couples therapy for adults",
      specialties: ["Career Stress", "Relationships", "Life Transitions", "Mental Health"]
    },
    {
      name: "Seniors (65+)",
      icon: "🌳",
      description: "Specialized care for older adults",
      specialties: ["Life Review", "Health Changes", "Loss & Grief", "Memory Issues"]
    }
  ];

  return (
    <>
      <SEOHead {...seoConfigs.specialties} />
      
      <div className="min-h-screen bg-[#e4fde1]" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        {/* Hero Section */}
        <div className="bg-[#e4fde1] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-[#254e70]" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
              Therapy Specialties
            </h1>
            <p className="text-xl text-[#254e70]/80 mb-8 max-w-3xl mx-auto">
              Find therapists who specialize in your specific needs. Our licensed professionals offer expertise across a wide range of mental health concerns.
            </p>
            <Link 
              to="/find-therapists"
              className="inline-flex items-center gap-2 bg-[#254e70] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#9dacff] hover:text-white transition-all duration-200"
            >
              Find Specialized Therapists
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Specialties Grid */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                Find Your Specialty
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Browse our comprehensive list of therapy specializations to find the right support for your specific needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {specialties.map((specialty) => (
                <div key={specialty.id} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="text-center mb-6">
                    <div className="text-4xl mb-4">{specialty.icon}</div>
                    <h3 className="text-xl font-bold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                      {specialty.name}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {specialty.description}
                    </p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-[#254e70] mb-2 uppercase tracking-wide">
                      Common Conditions
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {specialty.conditions.map((condition, index) => (
                        <span key={index} className="text-xs bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full">
                          {condition}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-[#254e70] mb-2 uppercase tracking-wide">
                      Treatment Approaches
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {specialty.approaches.map((approach, index) => (
                        <span key={index} className="text-xs bg-[#9dacff]/20 text-[#254e70] px-2 py-1 rounded-full">
                          {approach}
                        </span>
                      ))}
                    </div>
                  </div>

                  <Link 
                    to={`/find-therapists?specialty=${encodeURIComponent(specialty.name)}`}
                    className="block w-full text-center bg-[#254e70] text-white py-3 rounded-lg font-semibold hover:bg-[#1a3a52] transition-colors duration-200"
                  >
                    Find {specialty.name} Therapists
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Age Groups Section */}
        <div className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                Therapy by Age Group
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Find therapists who specialize in working with specific age groups and developmental stages.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {ageGroups.map((group, index) => (
                <div key={index} className="bg-[#e4fde1] rounded-lg p-6 text-center">
                  <div className="text-3xl mb-3">{group.icon}</div>
                  <h3 className="text-lg font-semibold text-[#254e70] mb-2" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    {group.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {group.description}
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {group.specialties.map((specialty, specIndex) => (
                      <li key={specIndex} className="flex items-center justify-center gap-1">
                        <span className="w-1 h-1 bg-[#9dacff] rounded-full"></span>
                        {specialty}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* How to Choose Section */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                How to Choose the Right Specialty
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-8 shadow-md">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                  Consider Your Primary Concerns
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>What specific issues are you hoping to address?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Are there any symptoms that are particularly troubling?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Do you have a specific diagnosis you want to work on?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Are you seeking individual, couples, or family therapy?</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-8 shadow-md">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                  Look for Specialized Training
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Check therapist profiles for relevant certifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Look for experience with your specific concerns</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Consider therapists who mention your age group</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Ask about their training in specific treatment methods</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-[#9dacff] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
              Ready to Find Your Specialized Therapist?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Browse our directory of licensed professionals and find someone who specializes in your specific needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/find-therapists"
                className="inline-flex items-center justify-center gap-2 bg-[#254e70] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-[#254e70] transition-all duration-200"
              >
                Search by Specialty
              </Link>
              <Link 
                to="/how-it-works"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-[#9dacff] transition-all duration-200"
              >
                Learn How It Works
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SpecialtiesPage; 